import React from 'react';
import { connect } from 'react-redux';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import queryString from 'query-string';

import { getBrowserPreferredSystemLocale } from '~src/services/session';
import { UnauthorizedPagesContainer } from '~src/outer_modules/shared/shared.sc';
import SvgIcon from '~src/shared/SvgIcon';
import { promiseWithDelay } from '~src/utils';
import ProgressBar from '~src/shared/ProgressBar';
import errorCactus from '~src/images/error_cactus.png';
import logger from '~src/services/logger/LlsLogger';
import PrimaryButton from '~src/shared/buttons/basicButtons/PrimaryButton.sc';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import { logout, activateAccount, checkActivationStatus } from '~src/store/session/actions';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { routes } from '~src/routingConfig';
import { SuccessContainer } from '~src/outer_modules/shared/shared.sc';

import {
  ButtonWrapper,
  ErrorSubText,
  ErrorText,
  SpinningGear,
  SecondBtnLink,
  InfoText,
  InfoTextNote,
} from './activateAccount.sc';

let signUpRoutes: { [key: string]: string } = {
  de: 'https://www.loopline-systems.com/de/produkte/loop-now/signup',
  en: 'https://www.loopline-systems.com/en/products/loop-now/sign-up',
};

// Workaround to have the dev signup link not the website signup in dev mode
if (!import.meta.env.NODE_ENV || import.meta.env.NODE_ENV === 'development') {
  signUpRoutes = {
    de: '/signup',
    en: '/signup',
  };
}

interface DispatchProps {
  logout(): void;
  activateAccount(token?: string): ApiReduxResp<string>;
  checkActivationStatus(token: string, location?: string): ApiReduxResp<string>;
}

type Props = DispatchProps & WithTranslation & RouteComponentProps<any>;

enum ActivationError {
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  TOKEN_INVALID = 'INVALID_TOKEN',
  TIMEOUT = 'TIMEOUT',
  UNKNOWN = 'UNKNOWN',
}

interface State {
  error?: ActivationError;
  activatingAccount: boolean;
  progress: number;
}

class ActivateAccount extends React.Component<Props, State> {
  isUnmounted = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      error: undefined,
      activatingAccount: false,
      progress: 0,
    };
  }
  componentDidMount() {
    this.props.logout();
    const { auth_token } = queryString.parse(this.props.location.search) as { auth_token?: string };
    this.activateAccount(auth_token);
  }

  checkStatus = (statusToken: string): Promise<string> => {
    return new Promise(async (res, rej) => {
      const { location } = queryString.parse(this.props.location.search) as { location?: string };
      let link: string | null = null;
      let timeSpent = 0;
      const maxWaitTime = 2 * 60 * 1000;
      const timeDelay = 2000;

      while (link === null && timeSpent < maxWaitTime) {
        await promiseWithDelay(
          this.props
            .checkActivationStatus(statusToken, location)
            .then(resp => {
              if (resp) {
                const { rdir_url, activation_progress } = resp;
                if (rdir_url !== null) {
                  link = rdir_url;
                }
                if (!this.isUnmounted) {
                  this.setState({ progress: activation_progress });
                }
              } else {
                rej(resp);
                link = 'error';
                this.abort(ActivationError.UNKNOWN);
              }
            })
            .catch(error => {
              this.abort(ActivationError.UNKNOWN);
              logger.info('Error checking status', {
                error,
                info: {
                  componentStack: 'ActivateAccount.tsx',
                },
              });
              rej(error);
            }),
          timeDelay,
        );
        timeSpent += timeDelay;
      }

      if (!link) {
        return rej(ActivationError.TIMEOUT);
      }

      res(link);
    });
  };

  abort = (error: ActivationError) => {
    if (!this.isUnmounted) {
      this.setState({ error });
    }
    localStorage.removeItem('statToken');
    localStorage.removeItem('actToken');
  };

  activateAccount(token?: string) {
    this.setState({ activatingAccount: true });
    const preStatToken = localStorage.getItem('statToken');
    const preActToken = localStorage.getItem('actToken');
    if (preStatToken && preActToken === token) {
      this.checkStatus(preStatToken)
        .then(link => {
          this.props.history.replace(link);
        })
        .catch(err => {
          if (err === ActivationError.TIMEOUT) {
            return this.abort(err);
          }

          this.abort(ActivationError.TOKEN_INVALID);
        });
    } else {
      if (token) {
        localStorage.setItem('actToken', token);
      }
      this.props
        .activateAccount(token)
        .then(response => {
          if (!this.isUnmounted) {
            if (response.data.status_token) {
              const statusToken = response.data.status_token;
              localStorage.setItem('statToken', statusToken);
              this.checkStatus(statusToken)
                .then(link => {
                  this.props.history.replace(link);
                })
                .catch(() => this.abort(ActivationError.TOKEN_INVALID));
            } else {
              throw new Error(response.data);
            }
          }
        })
        .catch(err => {
          if (!this.isUnmounted) {
            this.setState({ error: ActivationError.TOKEN_INVALID });
            if (err.response.data.error.key === 'company_name_email_combination_error') {
              this.setState({ error: ActivationError.TOKEN_EXPIRED });
            }
            logger.info('Error activating Account', {
              error: err,
              info: {
                componentStack: 'index.js',
              },
            });
          }
          localStorage.removeItem('statToken');
          localStorage.removeItem('actToken');
        })
        .finally(() => {
          if (!this.isUnmounted) {
            this.setState({ activatingAccount: false });
          }
        });
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  render() {
    const { t } = this.props;
    let errorString = 'x.error.expired.token';
    const errorButton = {
      link: signUpRoutes[getBrowserPreferredSystemLocale()] || signUpRoutes.en,
      txtKey: 'x.activation.button.signup',
    };
    if (this.state.error === ActivationError.TOKEN_EXPIRED) {
      errorString = 'x.error.token.company_name_email_combination_error';
      errorButton.link = routes.login;
      errorButton.txtKey = 'x.activation.button.login';
    }

    return (
      <UnauthorizedPagesContainer>
        <SuccessContainer className="in_progress">
          {this.state.error ? (
            <>
              <img src={errorCactus} height={120} width={120} />
              <ErrorText data-translation="x.activation.error.header">
                {t('x.activation.error.header')}
              </ErrorText>
              <ErrorSubText data-translation={errorString}>{t(errorString)}</ErrorSubText>
              <ButtonWrapper>
                <a href={errorButton.link}>
                  <PrimaryButton $buttonColor="reddish" data-translation={errorButton.txtKey}>
                    {t(errorButton.txtKey)}
                  </PrimaryButton>
                </a>
                <SecondBtnLink to={`${routes.contact}?from=footer`}>
                  <SecondaryButton $buttonColor="reddish" data-translation="x.activation.button.contact">
                    {t('x.activation.button.contact')}
                  </SecondaryButton>
                </SecondBtnLink>
              </ButtonWrapper>
            </>
          ) : (
            <>
              <SpinningGear>
                <SvgIcon name="iconGear" width="50px" />
              </SpinningGear>
              <InfoText data-translation="x.activation.running">{t('x.activation.running')}</InfoText>
              <ProgressBar
                className="new_style"
                progress={this.state.progress * 100}
                width={350}
                height={10}
              />
              <InfoTextNote
                data-translation="x.activation.running.note"
                dangerouslySetInnerHTML={{
                  __html: t('x.activation.running.note'),
                }}
              />
            </>
          )}
        </SuccessContainer>
      </UnauthorizedPagesContainer>
    );
  }
}

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  logout: () => dispatch(logout),
  activateAccount: token => dispatch(activateAccount(token)),
  checkActivationStatus: (token, location?: string) => dispatch(checkActivationStatus(token, location)),
});

const ActivateAccountWithTranslate = withTranslation()(ActivateAccount);
const ActivateAccountSuperComponent = connect(null, mapDispatchToProps)(ActivateAccountWithTranslate);

export default ActivateAccountSuperComponent;
